import type { RouteCreateAPI, RouteGetAllAPI, RouteRegionAPI } from '@hero/api';
import type {
  ContractRouteRef,
  Currency,
  Order,
  OrderContractElement,
  OrderDestination,
} from '@hero/schema';

export type RouteRegionSearchHit = {
  _id: RouteRegionAPI['_id'];
  displayName?: string;
  type?: RouteRegionAPI['type'];
};

export type RouteRegionSearchFn = (
  query: string,
) => Promise<RouteRegionSearchHit[] | undefined>;

type RouteRegionApiClient = {
  common: {
    region: {
      search: (req: { q: string; limit?: number }) => Promise<RouteRegionSearchHit[]>;
      getAllCities: () => Promise<Array<{ _id: RouteRegionAPI['_id']; customCode?: string; displayName?: string }>>;
    };
  };
};

type RouteListApiClient = {
  routes: {
    getAll: (query: RouteGetAllAPI['req']) => Promise<RouteGetAllAPI['data']>;
  };
};

const idsMatch = (a: unknown, b: unknown): boolean => {
  if (!a || !b) return false;
  return String(a) === String(b);
};

/**
 * Find an existing active Route on the same (seller, buyer, lane,
 * vehicle) as the would-be create payload — so the save-as-route
 * flow can `update` it instead of triggering the backend's
 * vehicle-overlap protection, and so the price-edit UI can know up
 * front that a route already exists for this lane (even when the
 * order's `leg.route` snapshot is empty because the order was
 * created before the route, or with a one-time price).
 */
export const findMatchingExistingRoute = async (
  api: RouteListApiClient,
  payload: RouteCreateAPI['req'],
): Promise<RouteGetAllAPI['data'][number] | undefined> => {
  if (!payload.sellerId && !payload.buyerId) return undefined;
  try {
    const routes = await api.routes.getAll({
      sellerId: payload.sellerId,
      buyerId: payload.buyerId,
      vehicle: payload.vehicles[0],
    });
    const wantedDestIds = payload.destinations.map(d => String(d._id));
    return routes?.find(r => {
      if (!r.destinations || r.destinations.length !== wantedDestIds.length) return false;
      const haveDestIds = r.destinations.map(d => String(d._id));
      return wantedDestIds.every((id, i) => idsMatch(id, haveDestIds[i]));
    });
  } catch {
    return undefined;
  }
};

/**
 * Project a matched Route into the same `ContractRouteRef` shape the
 * order's leg carries when it was actually priced from a Route at
 * create time. Used by the price-edit drawer to merge `leg.route`
 * with the open-time lookup result so downstream UI ("save as route"
 * vs "update route") doesn't have to know which source the ref came
 * from.
 *
 * The `price` we synthesize here is the Route's **current** price,
 * which is the right thing to show as "the saved rate" in the
 * prompt — the original leg.route snapshot is frozen at create time
 * and would otherwise show a stale number if the rate has been
 * updated since.
 */
export const routeAsContractRouteRef = (
  route: RouteGetAllAPI['data'][number] | undefined,
): ContractRouteRef | undefined => {
  if (!route?._id) return undefined;
  return {
    routeId: route._id,
    price: route.price,
    agreementId: route.agreementId,
  };
};

const normalizeRegionQuery = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, '_');

const matchRegionCity = (
  query: string,
  city: { customCode?: string; displayName?: string },
): boolean => {
  const q = normalizeRegionQuery(query);
  return (
    normalizeRegionQuery(city.displayName ?? '') === q ||
    normalizeRegionQuery(city.customCode ?? '') === q
  );
};

/**
 * Region resolver for save-as-route: Atlas search first, then the
 * cached `getAllCities` list when search is unavailable or returns
 * nothing (common on mobile when query-array params fail validation).
 */
export const createRouteRegionSearchFn = (
  api: RouteRegionApiClient,
): RouteRegionSearchFn => {
  let citiesCache: Awaited<ReturnType<RouteRegionApiClient['common']['region']['getAllCities']>> | null =
    null;

  return async (query: string) => {
    try {
      const hits = await api.common.region.search({ q: query, limit: 5 });
      if (hits?.length) return hits;
    } catch {
      // fall through to getAllCities
    }

    try {
      if (!citiesCache) {
        citiesCache = await api.common.region.getAllCities();
      }
      const matched = citiesCache.filter(c => matchRegionCity(query, c));
      return matched.map(c => ({ _id: c._id, displayName: c.displayName, type: 'city' as const }));
    } catch {
      return undefined;
    }
  };
};

/**
 * Derive a Route lane endpoint from an order destination.
 *
 * Orders created after the relations-v2 cutover often have
 * `destination.region` unset (see `OrderRouteService.getNewDestination`).
 * Fall back to `address.city` when it carries a Region `_id` — the same
 * priority the backend uses in `OrderRouteGroupingUtilService`.
 */
export const getRouteRegionFromOrderDestination = (
  dest: OrderDestination,
): RouteRegionAPI | undefined => {
  if (dest.region?._id) {
    return {
      _id: dest.region._id,
      displayName: dest.region.displayName,
      type: dest.region.type,
    };
  }

  const city = dest.address?.city;
  if (city?._id) {
    return {
      _id: city._id,
      displayName: city.name,
      type: 'city',
    };
  }

  return undefined;
};

/**
 * Resolve a lane endpoint when the order destination has a city label but
 * no persisted Region `_id` (common on orders created after relations-v2).
 */
export const resolveRouteRegionFromOrderDestination = async (
  dest: OrderDestination,
  searchRegion: RouteRegionSearchFn,
): Promise<RouteRegionAPI | undefined> => {
  const direct = getRouteRegionFromOrderDestination(dest);
  if (direct) return direct;

  const query =
    dest.address?.city?.name?.trim() ||
    dest.address?.city?.customCode?.trim() ||
    dest.region?.displayName?.trim();
  if (!query || query.length < 2) return undefined;

  const hits = await searchRegion(query);
  const hit = hits?.[0];
  if (!hit?._id) return undefined;

  return {
    _id: hit._id,
    displayName: hit.displayName,
    type: hit.type,
  };
};

const buildRouteCreatePayloadFromRegions = (args: {
  order: Order;
  leg: OrderContractElement;
  price: number;
  currency: Currency;
  destinations: [RouteRegionAPI, RouteRegionAPI];
}): RouteCreateAPI['req'] | undefined => {
  const { order, leg, price, currency, destinations } = args;

  const vehicleType =
    order.vehicle?.classification?.type ?? order.load?.vehicles?.[0];
  if (!vehicleType) return undefined;

  const tripType = order.load?.tripType;
  const loadType = order.load?.category;
  if (!tripType || !loadType) return undefined;

  const sellerId = leg.seller?._id;
  const buyerId = leg.buyer?._id;
  if (!sellerId && !buyerId) return undefined;

  return {
    sellerId,
    buyerId,
    vehicles: [vehicleType],
    tripType,
    loadType,
    destinations,
    price,
    currency,
  };
};

/**
 * Async variant of {@link buildRouteCreatePayload}. When destinations
 * lack Region ids, resolves city names via region search / getAllCities
 * before building the create-Route payload.
 */
export const buildRouteCreatePayloadAsync = async (
  args: {
    order: Order;
    leg: OrderContractElement;
    price: number;
    currency: Currency;
  },
  searchRegion: RouteRegionSearchFn,
): Promise<RouteCreateAPI['req'] | undefined> => {
  const sync = buildRouteCreatePayload(args);
  if (sync) return sync;

  const dests = args.order.destinations ?? [];
  if (dests.length < 2) return undefined;

  const [first, last] = await Promise.all([
    resolveRouteRegionFromOrderDestination(dests[0], searchRegion),
    resolveRouteRegionFromOrderDestination(dests[dests.length - 1], searchRegion),
  ]);
  if (!first?._id || !last?._id) return undefined;

  return buildRouteCreatePayloadFromRegions({
    ...args,
    destinations: [first, last],
  });
};

/**
 * Build the create-Route payload from an order + viewer leg. Returns
 * `undefined` if any required field can't be derived (no lane regions,
 * no vehicle type, no party ids, etc.) — the caller should treat that
 * as "we can't save this as a Route" and skip the side-effect.
 *
 * Note: the order shape only carries one `vehicle` (the assigned
 * one), not the originally-requested vehicle TYPES list. We seed the
 * Route with a single-vehicle array derived from the assigned
 * vehicle's classification, which is the lane's effective vehicle for
 * this trip. Users can broaden the vehicle list later from the Routes
 * page.
 */
export const buildRouteCreatePayload = (args: {
  order: Order;
  leg: OrderContractElement;
  price: number;
  currency: Currency;
}): RouteCreateAPI['req'] | undefined => {
  const { order, leg, price, currency } = args;

  const destinations = (order.destinations ?? [])
    .map(getRouteRegionFromOrderDestination)
    .filter((r): r is RouteRegionAPI => !!r?._id);
  if (destinations.length < 2) return undefined;

  return buildRouteCreatePayloadFromRegions({
    order,
    leg,
    price,
    currency,
    destinations: [destinations[0], destinations[destinations.length - 1]],
  });
};
